import { closeMapHelpTooltip, isMapHelpTooltipOpen } from "../map-help/utils";
import updateMapSearchParams from "../../utils/search-params-update";
import { extractCountryCodeFromMarketplaceId } from "../../utils/format";
import { getLatLngFromAddress } from "./geocoding";
import {
  HYPER_LOCAL_DISTANCE,
  SHOW_WALKING_DISTANCE_CIRCLE_ZOOM_LEVEL,
  WALKING_DISTANCE_BOUNDS_PADDING_DESKTOP,
  WALKING_DISTANCE_BOUNDS_PADDING_MOBILE,
} from "./const";
import { getDistance } from "./loadmore";
import { toggleWalkingDistanceTourtip } from "./marker";
import type { LatLng, LatLngBounds, UserLocationMarker } from "./types";

interface CenterUserLocationParams {
  isLoadingMoreItems: boolean;
  userLocationCoordinates: LatLng | null;
  userLocationMarker: UserLocationMarker | null;
  walkingDistanceBounds: LatLngBounds | null;
  map: google.maps.Map | undefined;
  hyperLocalItems: number[];
  closeMapHelp: () => void;
  isMobile?: boolean;
  forceCenter?: boolean;
}

let walkingDistanceTourtipTimeout: ReturnType<typeof setTimeout> | undefined;

function handleWalkingDistanceTourtip(locationMarker: UserLocationMarker): void {
  if (walkingDistanceTourtipTimeout) {
    clearTimeout(walkingDistanceTourtipTimeout);
  }

  walkingDistanceTourtipTimeout = setTimeout(() => {
    if (locationMarker && isMapHelpTooltipOpen()) {
      toggleWalkingDistanceTourtip(locationMarker, true, "top");
      closeMapHelpTooltip();
    }
  }, 0);
}

export function centerUserLocation({
  isLoadingMoreItems,
  userLocationCoordinates,
  userLocationMarker,
  walkingDistanceBounds,
  closeMapHelp,
  map,
  hyperLocalItems = [],
  isMobile,
  forceCenter,
}: CenterUserLocationParams) {
  if (isLoadingMoreItems || !userLocationMarker || !walkingDistanceBounds || !userLocationCoordinates || !map) return;

  const shouldShowWalkingDistanceTourtip = userLocationMarker && !forceCenter;

  if (shouldShowWalkingDistanceTourtip) {
    closeMapHelp();
    handleWalkingDistanceTourtip(userLocationMarker);
  }

  const hasLocalItemsInBounds = hyperLocalItems?.length > 0;

  const applyHyperLocal = forceCenter && hasLocalItemsInBounds;

  if (applyHyperLocal) {
    const paddingBounds = isMobile ? WALKING_DISTANCE_BOUNDS_PADDING_MOBILE : WALKING_DISTANCE_BOUNDS_PADDING_DESKTOP;
    map.fitBounds(walkingDistanceBounds, paddingBounds);
  } else if (forceCenter) {
    map.setCenter(userLocationCoordinates);
    map.setZoom(SHOW_WALKING_DISTANCE_CIRCLE_ZOOM_LEVEL);
  }
}

export function isDistanceInHyperLocalRange(): boolean {
  return parseInt(getDistance()) <= HYPER_LOCAL_DISTANCE;
}

interface RestorePreviousLocationParams {
  map: google.maps.Map;
  apiKey: string;
  marketplaceId?: string;
  previousZipcode: string;
  previousZoom?: number;
  previousDistance?: string;
  setProgramaticallyChangingBounds: (value: boolean) => void;
}

export async function restorePreviousLocation({
  map,
  apiKey,
  marketplaceId,
  previousZipcode,
  previousZoom,
  previousDistance,
  setProgramaticallyChangingBounds,
}: RestorePreviousLocationParams): Promise<void> {
  try {
    const country = marketplaceId ? extractCountryCodeFromMarketplaceId(marketplaceId) : "";
    const coordinates = await getLatLngFromAddress(apiKey, `${previousZipcode}, ${country}`);

    setProgramaticallyChangingBounds(true);
    map.setCenter(coordinates);

    if (previousZoom !== undefined) {
      map.setZoom(previousZoom);
    }

    if (previousDistance) {
      const params = new URLSearchParams(window.location.search);
      const sources = params.get("sources") ?? "";
      updateMapSearchParams({
        zipcode: previousZipcode,
        distance: previousDistance,
        sources,
      });
    }

    setProgramaticallyChangingBounds(false);
  } catch (error) {
    console.error("Failed to restore previous location:", error);
    setProgramaticallyChangingBounds(false);
  }
}
